import DateInput from "components/elements/DateInput";
import dataProvider from "core/DataProvider";
import { useState } from "react";
import toast from "react-hot-toast";

const { default: IconButton } = require("components/elements/IconButton");

const UploadRateCard = props => {
  const [formVisible, setFormVisible] = useState(false);
  const [periodFrom, setPeriodFrom] = useState("");
  const [periodTo, setPeriodTo] = useState("");
  const [csvFile, setCSVFile] = useState(null);

  const reader = new FileReader();

  // Function to handle CSV file upload and validation
  const addRateCardHandler = () => {
    // Check if both Period From and Period To are not empty
    if (!periodFrom || !periodTo) {
      toast.error("Period From and Period To cannot be blank");
      return;
    }
    // Convert the string dates to JavaScript Date objects for comparison
    const fromDate = new Date(periodFrom);
    const toDate = new Date(periodTo);

    // Validate if Period To is greater than Period From
    if (toDate <= fromDate) {
      toast.error("Period To should be greater than Period From");
      return;
    }

    // Check that Period From starts in April and Period To ends in March
    const fromMonth = fromDate.getMonth() + 1; // getMonth() is zero-based, so add 1
    const toMonth = toDate.getMonth() + 1;
    const fromYear = fromDate.getFullYear();
    const toYear = toDate.getFullYear();

    // Validate for financial year (April to March)
    if (fromMonth !== 4 || toMonth !== 3 || toYear !== fromYear + 1) {
      toast.error(
        "Period From must start in April, and Period To must end in March of the next year"
      );
      return;
    }

    reader.addEventListener("load", event => {
      verifyFile();
    });
    reader.readAsText(csvFile); // Reads the content of the CSV file
  };

  // Function to verify the file content
  const verifyFile = () => {
    const finalData = [];
    const data = reader.result.trim(); // Removing any leading/trailing whitespace from the file content
    const rows = data.split("\n"); // Splitting the content into rows based on new lines

    // If rows are empty, the file is considered invalid
    if (!rows) {
      toast.error("Invalid file: no data");
      return;
    }

    // Iterating through each row in the CSV
    for (let i = 0; i < rows.length; i++) {
      const cols = rows[i].split(",").map(x => x.trim()); // Splitting each row into columns and trimming spaces

      // **Header Validation**: Check if the first row matches expected headers
      if (i === 0) {
        if (
          cols[0] !== "spot_from" ||
          cols[1] !== "spot_to" ||
          cols[2] !== "fixed_rate" ||
          cols[3] !== "additional_rate"
        ) {
          toast.error("Invalid file: invalid headers");
          return;
        }
      } else {
        // **Data Validation**: Validate that all rows contain numeric values
        const [spot_from, spot_to, fixed_rate, additional_rate] = cols;

        // If any of the values are not valid numbers, throw an error
        if (
          isNaN(parseFloat(spot_from)) ||
          isNaN(parseFloat(spot_to)) ||
          isNaN(parseFloat(fixed_rate)) ||
          isNaN(parseFloat(additional_rate))
        ) {
          toast.error("Invalid file: values are not proper numbers");
          return;
        }

        // If all values are valid, push the row into the finalData array (converting values to floats)
        finalData.push([
          parseFloat(spot_from),
          parseFloat(spot_to),
          parseFloat(fixed_rate),
          parseFloat(additional_rate),
        ]);
      }
    }

    // If validation passes, proceed to send the final data to the backend
    addRateCard(finalData);
  };

  // Function to send the final rate card data to the backend
  const addRateCard = async rates => {
    const data = {
      period_from: periodFrom,
      period_to: periodTo,
      rates: rates.map(rate => ({
        spot_from: parseFloat(rate[0]), // Convert string to float
        spot_to: parseFloat(rate[1]),
        fixed_rate: parseFloat(rate[2]),
        additional_rate: parseFloat(rate[3]),
      })),
      created_by: props.createdBy, // Assuming `createdBy` is passed in via props
    };

    try {
      await dataProvider.network.post("billing/add-rate-card", data);
      toast.success("Rate card added successfully");

      // Reset form
      setFormVisible(false);
      setPeriodFrom("");
      setPeriodTo("");
      setCSVFile(null);
    } catch (error) {
      console.error(error);
      toast.error("Failed to add rate card");
    }
  };

  // Handler for when the file is uploaded
  const fileHandler = e => {
    if (e.target.files.length) {
      const file = e.target.files[0];
      if (file.type && file.type === "text/csv") {
        setCSVFile(e.target.files[0]); // Only accepting CSV files
      } else {
        toast.error("Only CSV is allowed");
      }
    }
  };

  return (
    <div>
      <div className="flex">
        <div className="right flex flex-v-center">
          {formVisible ? (
            <>
              <IconButton text="Save" onClick={addRateCardHandler} />
              <IconButton
                text="Cancel"
                onClick={() => {
                  setFormVisible(false);
                }}
              />
            </>
          ) : (
            <IconButton
              icon="add"
              text="New Rate Card"
              onClick={() => setFormVisible(true)}
            />
          )}
        </div>
      </div>
      {formVisible && (
        <div className="box mtop">
          <div className="head pad-lr">
            <h3>Upload Rate Card</h3>
          </div>
          <div className="pad-lr flex mtop">
            <div className="el flex-full">
              <DateInput
                value={periodFrom}
                placeholder="Period From"
                onChange={val => setPeriodFrom(val)}
              />
            </div>
            <div className="el flex-full">
              <DateInput
                value={periodTo}
                placeholder="Period To"
                onChange={val => setPeriodTo(val)}
              />
            </div>
            <div className="el flex-full">
              <input type="file" onChange={fileHandler} accept=".csv" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadRateCard;
